import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { X, Trash2 } from 'lucide-react';
import { MentorChat } from '@/utils/api-pb';
import { cn } from '@/lib/utils';

import { useTranslation } from 'react-i18next';
interface ChatHistorySidebarProps {
  chats: MentorChat[];
  currentChat: MentorChat | null;
  chatToDelete: string | null;
  setChatToDelete: (id: string | null) => void;
  canCreateNewChat: boolean;
  onClose: () => void;
  onSelectChat: (chat: MentorChat) => void;
  onCreateNewChat: () => void;
  onDeleteChat: (chatId: string) => void;
  getChatTitle: (chat: MentorChat) => string;
}

export default function ChatHistorySidebar({
  chats,
  currentChat,
  chatToDelete,
  setChatToDelete,
  canCreateNewChat,
  onClose,
  onSelectChat,
  onCreateNewChat,
  onDeleteChat,
  getChatTitle,
}: ChatHistorySidebarProps) {
  const { t } = useTranslation('chat');
  return (
    <>
      {/* Overlay for closing on outside click */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />
      <div className="fixed right-[16px] top-[16px] w-[280px] h-[calc(100vh-32px)] bg-white rounded-[16px] z-50 flex flex-col">
        <div className="p-[16px] flex flex-col gap-[16px] flex-1 overflow-y-auto">
          {/* Header */}
          <div className="flex items-center gap-[8px]">
            <div className="relative w-[24px] h-[24px] flex-shrink-0">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[14px] h-[14px]">
                <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 15 15">
                  <path
                    clipRule="evenodd"
                    d="M12.4499 12.449C10.3106 14.5885 7.14279 15.0508 4.55042 13.8519C4.16772 13.6978 3.85397 13.5733 3.55569 13.5733C2.72486 13.5782 1.69073 14.3838 1.15326 13.847C0.615797 13.3094 1.422 12.2745 1.422 11.4386C1.422 11.1403 1.3024 10.8322 1.14834 10.4487C-0.051112 7.85677 0.411785 4.6879 2.5511 2.54905C5.28205 -0.182901 9.71897 -0.182901 12.4499 2.54835C15.1858 5.28453 15.1809 9.71771 12.4499 12.449Z"
                    fillRule="evenodd"
                    stroke="#565858"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <span className="font-manrope font-semibold text-[14px] leading-[20px] text-[#0d0d0d] flex-1">
              {t('history.title')}
            </span>
            <button
              onClick={onClose}
              className="w-[24px] h-[24px] flex items-center justify-center hover:opacity-70 transition-opacity"
            >
              <X className="w-[14px] h-[14px] text-[#565858]" />
            </button>
          </div>

          {/* New Chat Button - Under header */}
          {canCreateNewChat ? (
            <button
              onClick={onCreateNewChat}
              className="flex items-center gap-[8px] px-[16px] py-[12px] rounded-[8px] w-full hover:bg-white transition-colors"
            >
              <div className="relative w-[24px] h-[24px] flex-shrink-0">
                <div className="absolute left-[4px] top-[4px] w-[16px] h-[16px]">
                  <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 17 17">
                    <path d="M0.5 8.5H16.5M8.5 16.5V8.5L8.5 0.5" stroke="#565858" strokeLinecap="round" />
                  </svg>
                </div>
              </div>
              <span className="font-manrope font-semibold text-[14px] leading-[20px] text-[#565858]">
                {t('history.newChat')}
              </span>
            </button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-[8px] px-[16px] py-[12px] rounded-[8px] w-full opacity-60 cursor-not-allowed">
                  <div className="relative w-[24px] h-[24px] flex-shrink-0">
                    <div className="absolute left-[4px] top-[4px] w-[16px] h-[16px]">
                      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 17 17">
                        <path d="M0.5 8.5H16.5M8.5 16.5V8.5L8.5 0.5" stroke="#565858" strokeLinecap="round" />
                      </svg>
                    </div>
                  </div>
                  <span className="font-manrope font-semibold text-[14px] leading-[20px] text-[#565858]">
                    Новый чат
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {t('header.tooltip.newChatLimit')}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Chat list */}
          <div className="flex flex-col gap-[8px]">
            {chats.map((chat) => (
              <div
                key={chat.id}
                className={cn(
                  'group flex items-center gap-[8px] px-[16px] py-[8px] rounded-[8px] w-full text-left transition-colors cursor-pointer',
                  currentChat?.id === chat.id ? 'bg-[#eeeeee]' : 'hover:bg-white'
                )}
              >
                <button
                  onClick={() => onSelectChat(chat)}
                  className="flex-1 text-left min-w-0 overflow-hidden"
                >
                  <span className="font-manrope font-semibold text-[14px] leading-[20px] text-[#0d0d0d] truncate block">
                    {getChatTitle(chat)}
                  </span>
                </button>

                <AlertDialog open={chatToDelete === chat.id} onOpenChange={(open: boolean) => !open && setChatToDelete(null)}>
                  <AlertDialogTrigger asChild>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setChatToDelete(chat.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[#f8f8f8] rounded-[8px] transition-all flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4 text-[#565858]" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('history.deleteTitle')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('history.deleteDescription')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('history.deleteCancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onDeleteChat(chat.id)}
                        className="bg-red-500 hover:bg-red-600"
                      >
                        {t('history.deleteConfirm')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}

            {chats.length === 0 && (
              <p className="font-segoe text-[14px] leading-[20px] text-[#565858] text-center py-[32px]">
                {t('history.emptyList')}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
